import { P, usePermission } from "@crm/access/authorization/client";
import { useAuth } from "@crm/access/hooks/use-auth";
import { clientPromiseEffect } from "@crm/effect/client/promise-effect";
import { browserEffectRuntime } from "@crm/effect/client/runtime";
import type { AppError } from "@crm/errors/index";
import { ListPageShell, PageHeader } from "@crm/ui/components/shared";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@crm/ui/components/ui/alert-dialog";
import { Button } from "@crm/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@crm/ui/components/ui/dialog";
import { useHasWpEnrollment } from "@crm/ui/hooks/use-student-enrollments";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Effect, Option, Result } from "effect";
import * as AsyncResult from "effect/unstable/reactivity/AsyncResult";
import { Download, Loader2, Search, Trash2, Upload, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { toast } from "sonner";
import {
  useDeleteDesignTemplate,
  useDesignTemplateFile,
  useDesignTemplates,
  useUploadDesignTemplate,
} from "#app/design-templates";
import { rpcCaller } from "@crm/rpc-client";
import { DesignTemplatesRpc } from "@crm/rpc-contract/groups/design-templates";
import type { DesignTemplateListItem } from "#app/design-templates/design-templates.effects";
import { displayDesignTemplateName } from "./-lib/display-name";

const designTemplates = rpcCaller(DesignTemplatesRpc);

// 顶层定义正则表达式以避免性能问题
const HTML_FILE_REGEX = /\.(html|htm)$/i;

// 每页显示数量
const PAGE_SIZE = 12;

function promiseEffect<A>(run: () => PromiseLike<A>) {
  return clientPromiseEffect("设计参考操作失败", run);
}

function runDesignTemplateEffect<A>(effect: Effect.Effect<A, AppError>): Promise<A> {
  return browserEffectRuntime.runPromise(effect);
}

export const Route = createFileRoute("/_protected/site-tools/_layout/references/")({
  head: () => ({}),
  component: DesignReferencesPage,
});

function DesignReferencesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = usePermission(P.DESIGN_TEMPLATE_WRITE_ALL);
  const hasWpEnrollment = useHasWpEnrollment();
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState<DesignTemplateListItem | null>(null);
  const [keyword, setKeyword] = useState("");

  // 客户端分页状态
  const [displayCount, setDisplayCount] = useState(PAGE_SIZE);

  // 使用 effect-atom 获取数据
  const templatesResult = useDesignTemplates();
  const allTemplates = AsyncResult.value(templatesResult).pipe(Option.getOrElse(() => []));

  // 检查学员权限：student 角色且未绑定 studentId 或无建站课
  const isStudentWithNoAccess = user?.role === "student" && !(user.studentId && hasWpEnrollment);

  // 按创建时间倒序 + 关键词过滤
  const sortedTemplates = useMemo(() => {
    const sorted = [...allTemplates].sort((a, b) => {
      // WireJson 序列化后 createdAt 为 ISO 字符串，new Date 兼容 Date | string
      const timeA =
        a.createdAt instanceof Date
          ? a.createdAt.getTime()
          : Date.parse(a.createdAt ?? "1970-01-01T00:00:00.000Z");
      const timeB =
        b.createdAt instanceof Date
          ? b.createdAt.getTime()
          : Date.parse(b.createdAt ?? "1970-01-01T00:00:00.000Z");
      return timeB - timeA;
    });
    const query = keyword.trim().toLowerCase();
    if (!query) {
      return sorted;
    }
    return sorted.filter((template) => template.fileName.toLowerCase().includes(query));
  }, [allTemplates, keyword]);

  // 当前显示的模板
  const templates = useMemo(
    () => sortedTemplates.slice(0, displayCount),
    [sortedTemplates, displayCount],
  );

  const totalCount = sortedTemplates.length;
  const hasMore = displayCount < totalCount;

  // 加载更多
  const loadMore = useCallback(() => {
    setDisplayCount((prev) => Math.min(prev + PAGE_SIZE, totalCount));
  }, [totalCount]);

  // 点击卡片跳转到预览页
  const handleCardClick = useCallback(
    (templateId: string) => {
      void navigate({
        to: "/site-tools/references/$id",
        params: { id: templateId },
      });
    },
    [navigate],
  );

  return (
    <ListPageShell>
      <PageHeader
        action={
          canManage
            ? {
                label: "上传参考",
                icon: <Upload className="size-4" />,
                onClick: () => setIsUploadDialogOpen(true),
              }
            : undefined
        }
        description="真实 B2B 外贸站的设计实例。看到喜欢的，预览、下载 HTML，直接在你的站点上复刻。"
        title="设计参考"
      />

      {isStudentWithNoAccess ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-warning bg-warning-soft p-12 text-center">
          <div className="mb-4 text-6xl">🔒</div>
          <h2 className="mb-2 font-bold text-2xl text-warning">需要建站课学员身份</h2>
          <p className="text-warning">
            {user?.studentId
              ? "设计参考仅对参加建站课的学员开放，请联系管理员了解详情。"
              : "您的账号尚未关联学员档案，请联系管理员添加。"}
          </p>
        </div>
      ) : (
        <>
          {/* 搜索工具栏 */}
          {allTemplates.length > 0 && (
            <div className="mb-6 flex items-center gap-3">
              <div className="flex w-64 items-center gap-2 rounded-[10px] border border-border bg-card px-3.5 py-2 text-[13px] text-ink-3">
                <Search size={15} />
                <input
                  className="w-full bg-transparent text-ink outline-none placeholder:text-ink-3"
                  onChange={(e) => {
                    setKeyword(e.target.value);
                    setDisplayCount(PAGE_SIZE);
                  }}
                  placeholder="搜索参考…"
                  value={keyword}
                />
              </div>
              <span className="text-[12.5px] text-ink-3">{totalCount} 个实例</span>
            </div>
          )}

          {totalCount === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-12 text-center">
              <p className="text-muted-foreground">
                {keyword.trim() ? "没有匹配的参考" : "暂无参考"}
              </p>
              {canManage && !keyword.trim() && (
                <Button
                  className="mt-4"
                  onClick={() => setIsUploadDialogOpen(true)}
                  variant="outline"
                >
                  <Upload className="mr-2 size-4" />
                  上传第一个参考
                </Button>
              )}
            </div>
          ) : (
            <>
              {/* 参考卡片网格 - 固定宽度卡片自适应排列 */}
              <div className="flex flex-wrap gap-6">
                {templates.map((template) => (
                  <TemplateCard
                    canManage={canManage}
                    key={template.id}
                    onClick={() => handleCardClick(template.id)}
                    onDelete={() => setDeletingTemplate(template)}
                    template={template}
                  />
                ))}
              </div>

              {/* 无限滚动触发器 */}
              <InfiniteScrollTrigger
                hasMore={hasMore}
                loadedCount={templates.length}
                loadMore={loadMore}
                totalCount={totalCount}
              />
            </>
          )}
        </>
      )}

      {/* 上传对话框 */}
      <UploadDialog isOpen={isUploadDialogOpen} onClose={() => setIsUploadDialogOpen(false)} />

      {/* 删除确认对话框 */}
      <DeleteDialog
        onOpenChange={(open) => !open && setDeletingTemplate(null)}
        template={deletingTemplate}
      />
    </ListPageShell>
  );
}

/**
 * 无限滚动触发器（客户端分页）
 */
function InfiniteScrollTrigger({
  hasMore,
  loadMore,
  totalCount,
  loadedCount,
}: {
  hasMore: boolean;
  loadMore: () => void;
  totalCount: number;
  loadedCount: number;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: "200px",
  });

  useEffect(() => {
    if (inView && hasMore && !isLoading) {
      setIsLoading(true);
      // 模拟加载延迟，让用户看到加载状态
      setTimeout(() => {
        loadMore();
        setIsLoading(false);
      }, 100);
    }
  }, [inView, hasMore, isLoading, loadMore]);

  if (!hasMore && loadedCount > 0) {
    return (
      <div className="py-8 text-center text-muted-foreground text-sm">
        已显示全部 {totalCount} 个参考
      </div>
    );
  }

  return (
    <div className="py-8 text-center" ref={ref}>
      {isLoading ? (
        <div className="flex items-center justify-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>加载中...</span>
        </div>
      ) : hasMore ? (
        <div className="text-muted-foreground text-sm">
          显示 {loadedCount} / 共 {totalCount} 个
        </div>
      ) : null}
    </div>
  );
}

const IFRAME_WIDTH = 1200;
const IFRAME_HEIGHT = 900;
const PREVIEW_WIDTH = 350;
const PREVIEW_HEIGHT = Math.round(IFRAME_HEIGHT * (PREVIEW_WIDTH / IFRAME_WIDTH));
const PREVIEW_SCALE = PREVIEW_WIDTH / IFRAME_WIDTH;

function TemplateCardSkeleton() {
  return (
    <div
      className="overflow-hidden rounded-lg border bg-card"
      style={{ width: `${PREVIEW_WIDTH}px` }}
    >
      <div className="flex items-center gap-1.5 border-border border-b px-3 py-2">
        <span className="h-2 w-2 rounded-full bg-muted" />
        <span className="h-2 w-2 rounded-full bg-muted" />
        <span className="h-2 w-2 rounded-full bg-muted" />
      </div>
      <div
        className="animate-pulse bg-muted"
        style={{ width: `${PREVIEW_WIDTH}px`, height: `${PREVIEW_HEIGHT}px` }}
      >
        <div className="flex h-full items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/50" />
        </div>
      </div>
      <div className="flex items-center justify-between p-4">
        <div className="h-4 w-24 animate-pulse rounded bg-muted" />
        <div className="flex gap-2">
          <div className="h-8 w-8 animate-pulse rounded bg-muted" />
          <div className="h-8 w-8 animate-pulse rounded bg-muted" />
        </div>
      </div>
    </div>
  );
}

/**
 * 参考卡片组件
 *
 * 列表卡片只从列表接口拿 metadata；进入视口后按卡片懒加载 HTML 预览。
 * - 固定预览尺寸 350x262px
 * - 浏览器窗口样式：顶部地址栏 + 缩略页面
 */
function TemplateCard({
  template,
  canManage,
  onClick,
  onDelete,
}: {
  template: DesignTemplateListItem;
  canManage: boolean;
  onClick: () => void;
  onDelete: () => void;
}) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const { ref: cardRef, inView } = useInView({
    threshold: 0,
    rootMargin: "200px",
    triggerOnce: true,
  });

  const fileResult = useDesignTemplateFile(template.id, inView);
  const file = AsyncResult.value(fileResult).pipe(Option.getOrUndefined);
  const content = file?.content ?? null;
  const displayName = displayDesignTemplateName(template.fileName, content);

  // 下载参考
  const downloadTemplate = (e: React.MouseEvent) => {
    e.stopPropagation(); // 阻止冒泡到卡片点击

    void runDesignTemplateEffect(
      Effect.gen(function* () {
        const htmlContent =
          content ??
          (yield* promiseEffect(() => designTemplates.getDesignTemplateFile({ id: template.id })))
            .content;

        yield* Effect.sync(() => {
          const blob = new Blob([htmlContent], { type: "text/html" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = template.fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);

          toast.success("下载成功");
        });
      }),
    ).catch(() => {
      toast.error("下载失败");
    });
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation(); // 阻止冒泡到卡片点击
    onDelete();
  };

  return (
    <div ref={cardRef}>
      {inView || content ? (
        <div
          className="group hover:-translate-y-0.5 overflow-hidden rounded-xl border bg-card transition-all hover:shadow-m"
          style={{ width: `${PREVIEW_WIDTH}px` }}
        >
          {/* 浏览器地址栏 */}
          <div className="flex items-center gap-1.5 border-border border-b bg-card px-3 py-2">
            <span className="h-2 w-2 rounded-full bg-border" />
            <span className="h-2 w-2 rounded-full bg-border" />
            <span className="h-2 w-2 rounded-full bg-border" />
            <span className="ml-1.5 flex h-4 flex-1 items-center truncate rounded-full bg-muted px-2.5 font-mono text-[9px] text-ink-3">
              {displayName}
            </span>
          </div>

          <button
            className="relative block cursor-pointer overflow-hidden bg-muted"
            onClick={onClick}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            style={{
              width: `${PREVIEW_WIDTH}px`,
              height: `${PREVIEW_HEIGHT}px`,
            }}
            type="button"
          >
            <iframe
              className="pointer-events-none absolute left-0 origin-top-left"
              onLoad={() => setIsLoaded(true)}
              sandbox="allow-scripts"
              src={`/api/design-templates/${template.id}`}
              style={{
                width: `${IFRAME_WIDTH}px`,
                height: `${IFRAME_HEIGHT}px`,
                transform: `scale(${PREVIEW_SCALE})`,
                transformOrigin: "top left",
                top: isHovering ? `${PREVIEW_HEIGHT - IFRAME_HEIGHT * PREVIEW_SCALE}px` : "0",
                transition: isHovering ? "top 3s ease-in-out" : "top 0.3s ease-out",
              }}
              title={displayName}
            />

            {!isLoaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-muted/80">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}

            <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-chrome/50 opacity-0 transition-opacity group-hover:opacity-100">
              <span className="rounded-lg bg-brand px-3.5 py-1.5 font-semibold text-white text-xs">
                打开预览
              </span>
            </div>
          </button>

          <div className="flex items-center justify-between p-4">
            <span className="font-medium text-sm" title={displayName}>
              {displayName.length > 20 ? `${displayName.slice(0, 20)}...` : displayName}
            </span>

            <div className="flex items-center gap-2">
              <Button onClick={downloadTemplate} size="icon-sm" variant="ghost">
                <Download className="size-4" />
              </Button>

              {canManage && !template.id.startsWith("builtin-design-system-") && (
                <Button onClick={handleDelete} size="icon-sm" variant="ghost">
                  <Trash2 className="size-4 text-destructive" />
                </Button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <TemplateCardSkeleton />
      )}
    </div>
  );
}

/**
 * 上传对话框（支持多文件）
 */
function UploadDialog({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [files, setFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = useUploadDesignTemplate();

  const handleUpload = () => {
    if (files.length === 0) {
      return;
    }

    setIsUploading(true);
    setUploadProgress({ current: 0, total: files.length });

    void runDesignTemplateEffect(
      Effect.gen(function* () {
        const outcomes = yield* Effect.forEach(
          files,
          (file, index) =>
            Effect.gen(function* () {
              yield* Effect.sync(() => {
                setUploadProgress({
                  current: index + 1,
                  total: files.length,
                });
              });
              const content = yield* promiseEffect(() => file.text());
              yield* promiseEffect(() =>
                uploadMutation.executePromise({
                  fileName: file.name,
                  content,
                }),
              );
            }).pipe(Effect.result),
          { concurrency: 1 },
        );

        const successCount = outcomes.filter(Result.isSuccess).length;
        const failCount = outcomes.length - successCount;

        yield* Effect.sync(() => {
          if (failCount === 0) {
            toast.success(`成功上传 ${successCount} 个参考`);
          } else if (successCount === 0) {
            toast.error("上传失败");
          } else {
            toast.warning(`上传完成：${successCount} 成功，${failCount} 失败`);
          }

          onClose();
          setFiles([]);
        });
      }),
    ).finally(() => {
      setIsUploading(false);
      setUploadProgress(null);
    });
  };

  const handleFilesSelect = (selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) {
      return;
    }

    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    for (const file of Array.from(selectedFiles)) {
      if (HTML_FILE_REGEX.test(file.name)) {
        // 检查是否已存在（按文件名去重）
        if (!files.some((f) => f.name === file.name)) {
          validFiles.push(file);
        }
      } else {
        invalidFiles.push(file.name);
      }
    }

    if (invalidFiles.length > 0) {
      toast.error(`以下文件不是 HTML 格式：${invalidFiles.join(", ")}`);
    }

    if (validFiles.length > 0) {
      setFiles((prev) => [...prev, ...validFiles]);
    }
  };

  const removeFile = (fileName: string) => {
    setFiles((prev) => prev.filter((f) => f.name !== fileName));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleFilesSelect(e.dataTransfer.files);
  };

  // 关闭时重置状态
  const handleClose = () => {
    if (!isUploading) {
      setFiles([]);
      onClose();
    }
  };

  return (
    <Dialog onOpenChange={(open) => !open && handleClose()} open={isOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>上传参考</DialogTitle>
          <DialogDescription>选择或拖拽 HTML 文件上传到参考库（支持多选）</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* 拖拽上传区域 */}
          <button
            className={`flex w-full cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed p-6 transition-colors ${
              isDragging
                ? "border-primary bg-primary/5"
                : "border-muted-foreground/25 hover:border-muted-foreground/50"
            }`}
            disabled={isUploading}
            onClick={() => fileInputRef.current?.click()}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            type="button"
          >
            <Upload className="mb-2 size-6 text-muted-foreground" />
            <p className="font-medium text-sm">{isDragging ? "松开鼠标上传" : "拖拽文件到此处"}</p>
            <p className="mt-1 text-muted-foreground text-xs">或点击选择 HTML 文件（可多选）</p>
          </button>

          {/* 已选文件列表 */}
          {files.length > 0 && (
            <div className="max-h-40 space-y-2 overflow-y-auto">
              {files.map((file) => (
                <div
                  className="flex items-center justify-between rounded-md bg-muted/50 px-3 py-2"
                  key={file.name}
                >
                  <span className="truncate text-sm" title={file.name}>
                    {file.name}
                  </span>
                  <Button
                    className="ml-2 shrink-0"
                    disabled={isUploading}
                    onClick={() => removeFile(file.name)}
                    size="icon-sm"
                    variant="ghost"
                  >
                    <X className="size-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* 上传进度 */}
          {uploadProgress && (
            <div className="text-center text-muted-foreground text-sm">
              正在上传 {uploadProgress.current} / {uploadProgress.total}...
            </div>
          )}

          {/* 隐藏的 file input */}
          <input
            accept=".html,.htm"
            className="hidden"
            multiple
            onChange={(e) => handleFilesSelect(e.target.files)}
            ref={fileInputRef}
            type="file"
          />
        </div>

        <DialogFooter>
          <Button disabled={isUploading} onClick={handleClose} variant="outline">
            取消
          </Button>
          <Button disabled={files.length === 0 || isUploading} onClick={handleUpload}>
            {isUploading ? "上传中..." : `上传${files.length > 0 ? ` (${files.length})` : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * 删除确认对话框
 */
function DeleteDialog({
  template,
  onOpenChange,
}: {
  template: DesignTemplateListItem | null;
  onOpenChange: (open: boolean) => void;
}) {
  const deleteMutation = useDeleteDesignTemplate();

  const handleDelete = () => {
    if (!template) {
      return;
    }

    void runDesignTemplateEffect(
      Effect.gen(function* () {
        yield* promiseEffect(() => deleteMutation.executePromise(template.id));
        yield* Effect.sync(() => {
          toast.success("删除成功");
          onOpenChange(false);
        });
      }),
    ).catch(() => {
      toast.error("删除失败");
    });
  };

  return (
    <AlertDialog onOpenChange={onOpenChange} open={!!template}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>确认删除</AlertDialogTitle>
          <AlertDialogDescription>
            确定要删除参考 "{template?.fileName}" 吗？此操作无法撤销。
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleteMutation.isPending}>取消</AlertDialogCancel>
          <AlertDialogAction disabled={deleteMutation.isPending} onClick={handleDelete}>
            {deleteMutation.isPending ? "删除中..." : "删除"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
